import type { Timestamp } from 'firebase/firestore'

// Kanban columns — order matters for board display
export type DamageStatus =
  | 'reported'           // New damage filed
  | 'under_review'       // Being assessed internally
  | 'awaiting_estimate'  // Waiting for supplier quotes
  | 'estimate_received'  // Quotes in — decision pending
  | 'partially_approved' // Fleet manager approved some repair items, others pending/rejected
  | 'approved'           // Repair approved
  | 'in_repair'          // Active repair
  | 'quality_check'      // Repair done, QC pending
  | 'closed'             // Resolved
  | 'disputed'           // Contested — parked

export const DAMAGE_STATUS_ORDER: DamageStatus[] = [
  'reported',
  'under_review',
  'awaiting_estimate',
  'estimate_received',
  'partially_approved',
  'approved',
  'in_repair',
  'quality_check',
  'closed',
]

// ── Fases (agregação de apresentação) ────────────────────────────────────────
// Os 10 estados granulares continuam a ser a verdade nos dados (workflow
// automático, SLA, auto-aprovação e auditoria dependem deles), mas a UI
// mostra 5 fases agregadas — 10 colunas/filtros eram demasiados para o dia
// a dia. 'disputed' fica de fora da ordem normal: é excecional e só aparece
// quando existe.

export type DamagePhase =
  | 'analysis'   // reported, under_review
  | 'quoting'    // awaiting_estimate, estimate_received
  | 'approved'   // partially_approved, approved
  | 'repair'     // in_repair, quality_check
  | 'closed'     // closed
  | 'disputed'   // disputed

export const DAMAGE_PHASE_ORDER: DamagePhase[] = [
  'analysis',
  'quoting',
  'approved',
  'repair',
  'closed',
]

const PHASE_OF_STATUS: Record<DamageStatus, DamagePhase> = {
  reported: 'analysis',
  under_review: 'analysis',
  awaiting_estimate: 'quoting',
  estimate_received: 'quoting',
  partially_approved: 'approved',
  approved: 'approved',
  in_repair: 'repair',
  quality_check: 'repair',
  closed: 'closed',
  disputed: 'disputed',
}

export function damagePhaseOf(status: DamageStatus | string): DamagePhase {
  return PHASE_OF_STATUS[status as DamageStatus] ?? 'analysis'
}

// Estado concreto gravado quando um utilizador move uma avaria para uma fase
// (drop no kanban / seletor no detalhe). O workflow automático continua a
// usar os estados granulares — isto só se aplica a movimentos manuais.
export const DAMAGE_PHASE_CANONICAL_STATUS: Record<DamagePhase, DamageStatus> = {
  analysis: 'under_review',
  quoting: 'awaiting_estimate',
  approved: 'approved',
  repair: 'in_repair',
  closed: 'closed',
  disputed: 'disputed',
}

export type DamageSeverity = 'minor' | 'moderate' | 'major' | 'critical'

// Every value a stored damage may carry. 'door' predates the left/right door
// split and is kept only so older damages still validate and display — new
// damages pick from DAMAGE_LOCATION_OPTIONS, which leaves it out.
export const DAMAGE_LOCATIONS = [
  'front',
  'rear',
  'left_side',
  'right_side',
  'roof',
  'floor',
  'chassis',
  'landing_gear',
  'axle',
  'left_door',
  'right_door',
  'pallet_box_33',
  'pallet_box_rear',
  'door',
  'other',
] as const

export type DamageLocation = (typeof DAMAGE_LOCATIONS)[number]

export const DAMAGE_LOCATION_LABELS: Record<DamageLocation, string> = {
  front: 'Frontal',
  rear: 'Traseira',
  left_side: 'Lateral Esquerda',
  right_side: 'Lateral Direita',
  roof: 'Teto',
  floor: 'Piso',
  chassis: 'Chassis',
  landing_gear: 'Patolas',
  axle: 'Eixo',
  left_door: 'Porta Esquerda',
  right_door: 'Porta Direita',
  pallet_box_33: 'Porta-Paletes 33pal',
  pallet_box_rear: 'Porta-Paletes Traseiro',
  door: 'Porta',
  other: 'Outro',
}

// What the pickers offer, in display order. 'other' stays last as the escape
// hatch (and is what AI triage answers when it can't tell the side).
export const DAMAGE_LOCATION_OPTIONS: DamageLocation[] = [
  'front',
  'rear',
  'left_side',
  'right_side',
  'roof',
  'floor',
  'chassis',
  'landing_gear',
  'axle',
  'left_door',
  'right_door',
  'pallet_box_33',
  'pallet_box_rear',
  'other',
]

export interface DamagePhoto {
  id: string
  url: string
  key?: string              // R2 object key for deletion
  thumbnailUrl?: string
  caption?: string
  uploadedBy: string
  uploadedAt: Timestamp
}

export interface DamageAttachment {
  id: string
  name: string
  url: string
  mimeType: string
  sizeBytes: number
  uploadedBy: string
  uploadedAt: Timestamp
}

export interface Damage {
  id: string
  tenantId: string
  fleetId: string
  trailerId: string
  trailerPlate: string          // Denormalized for display without join

  title: string
  description: string
  status: DamageStatus
  severity: DamageSeverity
  location: DamageLocation
  locationDetail?: string

  reportedById: string
  reportedByName: string        // Denormalized
  assignedToId?: string
  assignedToName?: string       // Denormalized

  photos: DamagePhoto[]
  attachments: DamageAttachment[]

  estimatedCost?: number
  approvedCost?: number
  approvedEstimateId?: string   // Estimate accepted when the damage was approved
  autoApprovalReason?: string   // Why the last auto-approval evaluation routed to manual
  currency: string              // ISO 4217, e.g. 'EUR'

  // SLA tracking
  slaDeadline?: Timestamp
  slaStatus: 'on_track' | 'at_risk' | 'breached' | 'none'
  slaBreachedAt?: Timestamp

  // Workflow metadata
  resolvedAt?: Timestamp
  closedAt?: Timestamp

  invitedUserIds: string[]    // Firebase Auth UIDs of supplier users invited to quote

  reportedByRole?: 'supplier' | 'internal'  // Set when a supplier self-reported the damage

  tags: string[]

  createdAt: Timestamp
  updatedAt: Timestamp
}

export interface DamageComment {
  id: string
  damageId: string
  tenantId: string
  authorId: string
  authorName: string            // Denormalized
  authorPhotoUrl?: string
  text: string
  // Internal comments (false) are only ever shown to staff — the supplier's
  // mediated read (via the Worker) filters on this. Comments authored by a
  // supplier are always true. Absent on legacy comments predating this field.
  visibleToSupplier?: boolean
  attachments: DamageAttachment[]
  createdAt: Timestamp
  updatedAt: Timestamp
}

export interface EstimateLineItem {
  id: string
  description: string
  quantity: number
  unitPrice: number // sempre sem IVA
  total: number      // sempre sem IVA — soma dos itens = DamageEstimate.amountExVat
  // Per-item approval — absent/'pending' means not yet decided. Only used
  // when the fleet manager approves an estimate item-by-item instead of as
  // a whole (see DamageEstimate.status === 'partially_approved').
  status?: 'pending' | 'approved' | 'rejected'
  // Obrigatório quando status === 'rejected' — imposto na UI e nas mutações,
  // não no tipo (um item aprovado nunca tem motivo).
  rejectionReason?: string
  decidedBy?: string
  decidedByName?: string
  decidedAt?: Timestamp
}

export interface DamageEstimate {
  id: string
  damageId: string
  tenantId: string
  supplierId?: string
  supplierName: string
  contactEmail?: string
  contactPhone?: string
  // Quote/reference number as printed on the supplier's own orçamento
  // document (their numbering, not ours) — suppliers cite this on their
  // monthly invoice, which is how invoice lines get matched back to the
  // right repair. Captured at submission time (AI-extracted or typed in).
  supplierReference?: string
  amount: number          // total com IVA
  amountExVat?: number    // subtotal sem IVA
  vatRate?: number        // taxa IVA em % (ex: 23)
  vatAmount?: number      // valor do IVA
  approvedAmount?: number // soma dos lineItems aprovados (aprovação parcial)
  // Set when a fleet manager confirms a monthly-invoice line against this
  // estimate (see SupplierInvoice) — the estimate is then "faturada" and
  // shows as such in cost reporting. Absent until reconciled.
  invoiceId?: string
  currency: string
  status: 'pending' | 'partially_approved' | 'accepted' | 'rejected' | 'expired'
  // Justificação escrita por quem recusou. Nunca opcional na prática: rejeitar
  // sem dizer porquê deixava o fornecedor sem nada acionável e a obra sem
  // rasto do motivo. Fica também como comentário visível ao fornecedor.
  rejectionReason?: string
  // Marcado pelo Worker quando a recusa incide sobre um orçamento que já foi
  // faturado (invoiceId presente) — o dinheiro já circulou, por isso é preciso
  // nota de crédito. A nota em si é modelada na Fase 4; isto é o alerta.
  creditNoteRequired?: boolean
  creditNoteRequiredAt?: Timestamp
  validUntil?: Timestamp
  notes?: string
  lineItems?: EstimateLineItem[]
  attachments: DamageAttachment[]
  // Set when staff without approval authority flags this estimate for a
  // fleet_manager/tenant_admin to decide — a manual, discretionary signal,
  // not a workflow gate: the estimate can still be approved/rejected
  // directly regardless of this flag.
  escalated?: boolean
  escalatedBy?: string
  escalatedByName?: string
  escalatedAt?: Timestamp
  escalationNote?: string
  createdBy: string
  createdAt: Timestamp
  updatedAt: Timestamp
}

// Firestore activity log entry for audit trail
export interface DamageActivity {
  id: string
  damageId: string
  tenantId: string
  actorId: string
  actorName: string
  type:
    | 'status_change'
    | 'comment_added'
    | 'estimate_added'
    | 'estimate_item_status_change'
    | 'estimate_escalated'
    | 'photo_uploaded'
    | 'assigned'
    | 'field_updated'
    | 'sla_breached'
  payload: Record<string, unknown>
  createdAt: Timestamp
}
